import { FormControlLabel, TextField } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { useTrans } from "~/hooks/useTrans";
import { checkForErrorsState } from "~/utils/utils";
import { getCustomOutput } from "~/providers/api";
import { myUseState } from "~/hooks/myUseState";
import { MySwitch } from "~/mui/MySwitch";
import ValidTillInput from "~/mui/ValidTillInput";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	loading: boolean;
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface IStateNotification {
	isInternal: boolean;
	validTill: string;
	email: string;
	text: string;
}

type TStateNotificationErrors = {[key in keyof Pick<IStateNotification, "email" | "text">]: string; };

interface ICreateNotificationModal {
	lastUpdate: string;
	onClose?: () => void;
	onSave?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function CreateNotificationModal({
	lastUpdate = "",
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: ICreateNotificationModal) {
	const { trans } = useTrans();
	const { isCzech } = useLang();
	const { state, updateState } = myUseState<IState>({
		loading: true,
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateNotification, updateState: updateStateNotification } = myUseState<IStateNotification>({
		isInternal: true,
		validTill: "",
		email: "",
		text: "",
	});
	const { state: stateNotificationError, setState: setStateNotificationError } = myUseState<TStateNotificationErrors>({
		email: "",
		text: "",
	});

	function getWasChanged(newState: Partial<IStateNotification>): boolean {
		const stateData = {
			...stateNotification,
			...newState,
		};

		return stateData.validTill !== ""
			|| stateData.email !== ""
			|| stateData.text !== "";
	}

	function updateDataWithChange(newState: Partial<IStateNotification>) {
		const wasChanged = getWasChanged(newState);

		updateStateNotification(newState);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	function dialogOnSave() {
		const errorData = checkForErrorsState<IStateNotification, TStateNotificationErrors>(stateNotification, {
			email: { check: "email", msg: trans("userRole.error.email") },
			text: { check: "string-gt0", msg: trans("createNotificationModal.error.text") },
			validTill: { check: "string-gt0", msg: trans("createNotificationModal.error.validTill") },
		});

		setStateNotificationError(errorData.errorsState);

		// jsou tam chyby
		if (errorData.errorsCount > 0) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const sendData = getCustomOutput({
			lastUpdate,
		});

		if (sendData.noErrors) {
			onSave();
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: sendData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("createNotificationModal.title")} onClose={onClose} onSave={dialogOnSave} dialogLoading={state.dialogLoading} errorMsg={state.errorMsg}
		disableSave={!state.wasChanged} confirmClose={state.wasChanged} cancelCaption={ trans("cancel") } className="createNotificationModal" contentClassName="createNotificationModal__content">
		<div className="createNotificationModal__controls">
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={stateNotification.isInternal} onChange={event => updateDataWithChange({ isInternal: event.target.checked })} />}
				label={trans("createNotificationModal.internalNotification")}
			/>
			<ValidTillInput value={stateNotification.validTill} label={trans("createNotificationModal.date")} isCzech={isCzech} onChange={date => updateDataWithChange({ validTill: date.toISOString() })} />
			<TextField
				label={trans("createNotificationModal.email")}
				value={stateNotification.email}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ email: event.target.value })}
				color="success"
				autoComplete="off"
				error={stateNotificationError.email !== ""}
				helperText={stateNotificationError.email}
				size="small"
			/>
			<TextField
				label={trans("createNotificationModal.text")}
				value={stateNotification.text}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ text: event.target.value })}
				color="success"
				autoComplete="off"
				error={stateNotificationError.text !== ""}
				helperText={stateNotificationError.text}
				size="small"
				rows={3}
				multiline={true}
			/>
		</div>
	</MyDialog>;
}
